
import React, { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDashboard } from '@/hooks/useDashboard';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { trackDashboardViewed } from '@/utils/amplitudeEvents';
import MobileUserDashboard from '@/components/dashboard/MobileUserDashboard';
import DesktopUserDashboard from '@/components/dashboard/DesktopUserDashboard';

export default function UserDashboard() {
  const isMobile = useIsMobile();
  const dashboard = useDashboard();
  const { user, companyRole } = useAuth();
  const { currentCompany } = useCompany();
  
  // EVENT 16: Dashboard Viewed (fire once when data loads)
  const hasFiredDashboardEvent = React.useRef(false);
  useEffect(() => {
    if (!dashboard.loading && !hasFiredDashboardEvent.current) {
      hasFiredDashboardEvent.current = true;
      trackDashboardViewed({
        company_id: currentCompany?.company_id || null,
        user_id: user?.id || null,
        user_role: companyRole,
      });
    }
  }, [dashboard.loading]);

  // ProtectedRoute handles auth check and loading state - no need to duplicate here

  // Mobile-first layout with candidate data
  if (isMobile) {
    return (
      <MobileUserDashboard
        loading={dashboard.loading}
        dataLoadError={dashboard.dataLoadError}
        candidates={dashboard.behavioralSubmissions}
        onViewCandidate={dashboard.handleViewDetails}
        selectedSubmission={dashboard.selectedSubmission}
        isModalOpen={dashboard.isModalOpen}
        onCloseModal={dashboard.handleCloseModal}
      />
    );
  }

  return (
    <DesktopUserDashboard
      activeTab={dashboard.activeTab}
      setActiveTab={dashboard.setActiveTab}
      filtersExpanded={dashboard.filtersExpanded}
      tableExpanded={dashboard.tableExpanded}
      selectedIdealProfile={dashboard.selectedIdealProfile}
      setSelectedIdealProfile={dashboard.setSelectedIdealProfile}
      handleToggleTable={dashboard.handleToggleTable}
      handleToggleFilters={dashboard.handleToggleFilters}
      loading={dashboard.loading}
      dataLoadError={dashboard.dataLoadError}
      totalCounts={dashboard.totalCounts}
      behavioralSubmissions={dashboard.behavioralSubmissions}
      workBehaviorSubmissions={dashboard.workBehaviorSubmissions}
      idealProfiles={dashboard.idealProfiles}
      filteredBehavioralCount={dashboard.filteredBehavioralCount}
      filteredWorkBehaviorCount={dashboard.filteredWorkBehaviorCount}
      deletingProfileIds={dashboard.deletingProfileIds}
      searchTerm={dashboard.searchTerm}
      isSearching={dashboard.isSearching}
      currentFilters={dashboard.currentFilters}
      behavioralPagination={dashboard.behavioralPagination}
      workBehaviorPagination={dashboard.workBehaviorPagination}
      profilesPagination={dashboard.profilesPagination}
      handleViewDetails={dashboard.handleViewDetails}
      handleSubmissionDeleted={dashboard.handleSubmissionDeleted}
      handleDeleteProfile={dashboard.handleDeleteProfile}
      handleFiltersChange={dashboard.handleFiltersChange}
      handleSearchChange={dashboard.handleSearchChange}
      handleSignOut={dashboard.handleSignOut}
      selectedSubmission={dashboard.selectedSubmission}
      isModalOpen={dashboard.isModalOpen}
      handleCloseModal={dashboard.handleCloseModal}
      getPageTitle={dashboard.getPageTitle}
      subscribed={dashboard.subscribed}
      onManageSubscription={dashboard.handleManageSubscription}
      updateSubmissionStatus={dashboard.updateSubmissionStatus}
      profileFilter={dashboard.profileFilter}
      handleProfileFilterChange={dashboard.handleProfileFilterChange}
    />
  );
}
